// ════════════════════════════════════════════════════════════════════════════
// LpaEmptyState — empty state com 5 variants e CTA opcional
// ════════════════════════════════════════════════════════════════════════════

import React from "react";

export type LpaEmptyVariant =
  | "no-data"
  | "no-results"
  | "error"
  | "coming-soon"
  | "permission-denied";

export interface LpaEmptyStateProps {
  variant?: LpaEmptyVariant;
  title?: string;
  description?: string;
  cta?: {
    label: string;
    onClick: () => void;
    variant?: "primary" | "secondary" | "ghost";
  };
  /** Compact: padding menor para inline (dentro de tabelas) */
  compact?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const VARIANT_DEFAULTS: Record<
  LpaEmptyVariant,
  { icon: string; title: string; description: string }
> = {
  "no-data": {
    icon: "📭",
    title: "Nenhum dado disponível",
    description: "Quando houver registros, eles aparecerão aqui.",
  },
  "no-results": {
    icon: "🔍",
    title: "Nenhum resultado encontrado",
    description: "Tente ajustar os filtros ou termos da busca.",
  },
  error: {
    icon: "⚠",
    title: "Erro ao carregar",
    description: "Não foi possível carregar os dados. Tente novamente.",
  },
  "coming-soon": {
    icon: "✨",
    title: "Em breve",
    description: "Esta funcionalidade está em desenvolvimento.",
  },
  "permission-denied": {
    icon: "🔒",
    title: "Acesso negado",
    description: "Você não tem permissão para visualizar este conteúdo.",
  },
};

export default function LpaEmptyState({
  variant = "no-data",
  title,
  description,
  cta,
  compact = false,
  className,
  style,
}: LpaEmptyStateProps): React.ReactElement {
  const defaults = VARIANT_DEFAULTS[variant];
  const t = title ?? defaults.title;
  const d = description ?? defaults.description;

  return (
    <div
      role="status"
      className={className}
      style={{
        padding: compact ? "20px 16px" : "40px 24px",
        textAlign: "center",
        background: "var(--lpa-surface)",
        border: "1px dashed var(--lpa-border)",
        borderRadius: "var(--lpa-radius-lg)",
        ...style,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          fontSize: compact ? 24 : 36,
          marginBottom: compact ? 8 : 12,
          opacity: 0.7,
        }}
      >
        {defaults.icon}
      </div>
      <h3
        style={{
          fontSize: compact ? "var(--lpa-text-lg)" : "var(--lpa-text-xl)",
          fontWeight: 600,
          color: "var(--lpa-text)",
          margin: 0,
          marginBottom: 6,
        }}
      >
        {t}
      </h3>
      <p
        style={{
          fontSize: "var(--lpa-text-md)",
          color: "var(--lpa-muted)",
          margin: 0,
          maxWidth: 360,
          marginLeft: "auto",
          marginRight: "auto",
          lineHeight: 1.5,
        }}
      >
        {d}
      </p>
      {cta && (
        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            onClick={cta.onClick}
            style={{
              fontFamily: "var(--lpa-font-sans)",
              fontWeight: 600,
              fontSize: "var(--lpa-text-md)",
              padding: "8px 16px",
              background:
                cta.variant === "ghost"
                  ? "transparent"
                  : cta.variant === "secondary"
                    ? "transparent"
                    : "var(--lpa-accent)",
              color:
                cta.variant === "ghost" || cta.variant === "secondary"
                  ? "var(--lpa-accent)"
                  : "var(--lpa-accent-text)",
              border: `1px solid ${
                cta.variant === "secondary" ? "var(--lpa-border)" : "transparent"
              }`,
              borderRadius: "var(--lpa-radius-md)",
              cursor: "pointer",
              transition: "all var(--lpa-tr-fast)",
            }}
          >
            {cta.label}
          </button>
        </div>
      )}
    </div>
  );
}
